import { useQuery } from 'react-query'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  useBinsService,
  useProductsService,
  useWheatCodesService,
} from 'services'
import { RootState } from 'store/store'
import { assignWheatCodeColors } from '../../utils/wheatCodeColors'
import { DATA_ANALYSIS_FILTERS } from '../../utils/consts'

export const useFilterData = (filtersList: DATA_ANALYSIS_FILTERS[]) => {
  const companyId = useSelector(
    (state: RootState) => state.user.profile?.company?.id
  )

  const { getBins } = useBinsService()
  const { getWheatCodes } = useWheatCodesService()
  const { getProducts } = useProductsService()

  // Determine what data we actually need
  const needsBins = filtersList.includes(DATA_ANALYSIS_FILTERS.BIN)
  const needsWheatCodes = filtersList.includes(DATA_ANALYSIS_FILTERS.WHEAT_CODE)
  const needsProducts =
    filtersList.includes(DATA_ANALYSIS_FILTERS.PRODUCTS) ||
    filtersList.includes(DATA_ANALYSIS_FILTERS.FLOUR_PRODUCT)

  // Conditional queries
  const { data: bins } = useQuery({
    queryKey: ['data-bins-list', companyId],
    queryFn: () => getBins(),
    enabled: needsBins && !!companyId,
  })

  const { data: wheatCodes } = useQuery({
    queryKey: ['data-wheat-codes-list', companyId],
    queryFn: () => getWheatCodes(),
    enabled: needsWheatCodes && !!companyId,
  })

  const { data: products } = useQuery({
    queryKey: ['data-products-list', companyId],
    queryFn: () => getProducts(),
    enabled: needsProducts && !!companyId,
  })

  const wheatCodeColorMap = useMemo(() => {
    if (!needsWheatCodes || !wheatCodes?.results) return {}

    const items = wheatCodes.results.map((w: any) => ({
      id: Number(w.id),
      code: String(w.code),
    }))
    return assignWheatCodeColors(items)
  }, [wheatCodes?.results, needsWheatCodes])

  return {
    bins: bins?.results || [],
    wheatCodes: wheatCodes?.results || [],
    products: products?.results || [],
    wheatCodeColorMap,
    isLoading: {
      bins: needsBins && bins === undefined,
      wheatCodes: needsWheatCodes && wheatCodes === undefined,
      products: needsProducts && products === undefined,
    },
  }
}
